import { invokeFunction, supabase } from '@/integrations/supabase/client';
import type { MerchantStatusId } from '@/shared/merchantStatus';
import { readFunctionErrorBody } from '@/utils/functionErrorBody';

// Changes a merchant's Status through the admin-only edge function, which also
// records it in the merchant's history (FIG-345 · UC-227).

// A flat shape (not a discriminated union): with strict mode off, TypeScript
// doesn't narrow unions on `ok` (tsconfig.app.json).
export interface SaveMerchantStatusResult {
  ok: boolean;
  /** Saved but its history entry wasn't written. */
  recorded?: boolean;
  /** Set when the Status changed since it was opened (409). */
  conflict?: { current: string };
  error?: string;
}

interface FunctionResponse {
  success?: boolean;
  recorded?: boolean;
  error?: string;
  /** Sent with a 409 when the Status changed since it was opened. */
  current?: string;
}

const fromBody = (body: FunctionResponse | null | undefined, fallback: string): SaveMerchantStatusResult => {
  if (body?.success) return { ok: true, recorded: body.recorded !== false };
  if (body?.current) return { ok: false, conflict: { current: body.current }, error: body.error ?? 'The status was changed by someone else' };
  return { ok: false, error: body?.error ?? fallback };
};

export async function saveMerchantStatus(
  organisationId: string,
  status: MerchantStatusId,
  expectedCurrent: MerchantStatusId,
): Promise<SaveMerchantStatusResult> {
  try {
    const { data } = await invokeFunction<FunctionResponse>(supabase, 'set-merchant-status-v1', {
      body: { organisationId, status, expectedCurrent },
    });
    return fromBody(data, 'Could not save the status');
  } catch (error) {
    const body = await readFunctionErrorBody<FunctionResponse>(error);
    return fromBody(body, error instanceof Error ? error.message : 'Could not save the status');
  }
}
